'use client';

import React, { useState, useEffect } from 'react';
import styles from './available-sections/styles/AvailableSections.module.css';

export default function CancelledSections({ courseId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCancelledSections = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/classes/${courseId}/cancelled-sections`);
        if (response.ok) {
          const data = await response.json();
          setSections(data.sections || []);
        } else if (response.status === 403) {
          // User doesn't have permission - silently handle
          setSections([]);
        } else {
          console.error('Failed to fetch cancelled sections');
        }
      } catch (err) {
        console.error('Failed to fetch cancelled sections:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchCancelledSections();
    }
  }, [courseId]);

  if (loading || !sections || sections.length === 0) {
    return null;
  }

  // Sort sections by cancelled date (most recently cancelled first)
  const sortedSections = [...sections].sort((a, b) => {
    const dateA = a.cancelledAt ? new Date(a.cancelledAt) : new Date(0);
    const dateB = b.cancelledAt ? new Date(b.cancelledAt) : new Date(0);
    return dateB - dateA;
  });

  const formatCancelledDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className={styles.cancelledSectionsContainer}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={styles.collapsibleHeader}
        type="button"
      >
        <span>
          {isExpanded ? 'Hide' : 'Show'} cancelled sections ({sections.length})
        </span>
        <span className={styles.expandIcon}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isExpanded && (
        <div className={styles.cancelledSectionsList}>
          {sortedSections.map((section) => {
            const sortedMeetings = [...(section.meetings || [])].sort(
              (a, b) => new Date(a.startTime) - new Date(b.startTime)
            );

            const headingDay = section.teachingDays
              ? section.teachingDays.split(',').join(', ')
              : sortedMeetings.length > 0
                ? new Date(sortedMeetings[0].startTime).toLocaleDateString('en-US', { weekday: 'long' })
                : 'Unknown';

            const headingTime = sortedMeetings.length > 0
              ? `${formatTime(sortedMeetings[0].startTime)} - ${formatTime(sortedMeetings[0].endTime)}`
              : 'No meetings scheduled';

            return (
              <div key={section.id} className={`${styles.timeSlot} ${styles.cancelledSection}`}>
                <div className={styles.timeSlotHeader}>
                  <div>
                    <h3>{headingDay}</h3>
                    <span>{headingTime}</span>
                  </div>
                  <div className={styles.cancelledBadge}>
                    Cancelled {formatCancelledDate(section.cancelledAt)}
                  </div>
                </div>

                <div className={styles.timeSlotDetails}>
                  <p>
                    <strong>Section ID:</strong> {section.id}
                  </p>
                  <p>
                    <strong>Meetings:</strong> {sortedMeetings.length}
                  </p>
                  {section.startDate && (
                    <p>
                      <strong>Was scheduled:</strong>{' '}
                      {new Date(section.startDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
